import { useState } from "react";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { assets } from "@/lib/site-data";
import { cn } from "@/lib/utils";

const pairs = [
  {
    main: { src: assets.oNasRekonstrukcia, alt: "Rekonštrukcia modernej kúpeľne" },
    overlay: { src: assets.oNasStavba, alt: "Realizácia obkladov a dlažby" },
  },
  {
    main: { src: assets.oNasKuchyne, alt: "Rekonštrukcia kuchyne na mieru" },
    overlay: { src: assets.oNasSluzby, alt: "Hrubá stavba a stavebné služby" },
  },
];

export function About() {
  const [index, setIndex] = useState(0);
  const go = (dir: number) => setIndex((i) => (i + dir + pairs.length) % pairs.length);

  return (
    <section
      id="o-nas"
      className="relative overflow-hidden bg-charcoal py-24 text-charcoal-foreground sm:py-32 md:py-40"
      style={{
        clipPath:
          "polygon(0% 14%, 8% 14%, 12% 0%, 100% 0%, 100% 100%, 82% 100%, 77% 86%, 0% 86%)",
      }}
    >
      <div className="container-artbaz">
        <div className="content-artbaz grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Carousel */}
          <Reveal className="relative">
            <div className="relative z-10 aspect-[4/3] w-full">
              {pairs.map((pair, i) => (
                <div
                  key={i}
                  className={cn(
                    "absolute inset-0 transition-opacity duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]",
                    i === index ? "opacity-100" : "pointer-events-none opacity-0",
                  )}
                >
                  <div className="absolute left-0 top-0 h-[78%] w-[80%] overflow-hidden rounded-2xl shadow-[var(--shadow-elevated)]">
                    <img src={pair.main.src} alt={pair.main.alt} className="h-full w-full object-cover" />
                  </div>
                  <div className="absolute bottom-0 right-0 h-[55%] w-[52%] overflow-hidden rounded-2xl border-4 border-charcoal shadow-[var(--shadow-elevated)]">
                    <img
                      src={pair.overlay.src}
                      alt={pair.overlay.alt}
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex items-center gap-3">
              <button
                type="button"
                onClick={() => go(-1)}
                aria-label="Predchádzajúce"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-primary hover:text-primary"
              >
                <ChevronLeft className="size-5" />
              </button>
              <button
                type="button"
                onClick={() => go(1)}
                aria-label="Ďalšie"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20 text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-primary hover:text-primary"
              >
                <ChevronRight className="size-5" />
              </button>
              <div className="ml-2 flex gap-2">
                {pairs.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Snímka ${i + 1}`}
                    onClick={() => setIndex(i)}
                    className={cn(
                      "h-1.5 rounded-full transition-all duration-300",
                      i === index ? "w-8 bg-primary" : "w-3 bg-white/25",
                    )}
                  />
                ))}
              </div>
            </div>
          </Reveal>

          {/* Text */}
          <Reveal delay={120}>
            <span className="eyebrow">O nás</span>
            <h2 className="mt-6 text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
              Viac než stavebná firma
            </h2>
            <div className="mt-6 space-y-5 text-base leading-relaxed text-charcoal-muted">
              <p>
                Už viac ako 8 rokov realizujeme rekonštrukcie, stavby a interiéry s dôrazom na
                kvalitu, detail a profesionálny prístup. Klientom pomáhame od prvého návrhu a výberu
                materiálov až po finálne odovzdanie projektu.
              </p>
              <p>
                Vďaka viac ako 300 realizáciám, neustálemu vzdelávaniu a individuálnemu prístupu
                vytvárame riešenia, ktoré spájajú funkčnosť, estetiku a dlhodobú hodnotu. Pravidelne
                školíme našich spolupracovníkov a každoročne sa zúčastňujeme konferencií lídrov
                slovenského a českého stavebníctva. Stavebníctvo je pre nás viac než práca – je to
                remeslo, ktoré nás baví, a umenie, v ktorom sa neustále zdokonaľujeme.
              </p>
            </div>
            <div className="mt-9">
              <Button asChild variant="hero" size="lg">
                <a href="/realizacia">
                  Pozrieť realizácie
                  <ArrowRight className="size-5" />
                </a>
              </Button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
